import { z } from 'zod';
import { compareDateOnly, DateOnlySchema } from '../../domain/date-only.ts';
import { newId } from '../../domain/id.ts';
import { IdSchema, INTERNAL_ONLY, VisibilitySchema } from '../../domain/entities/common.ts';
import {
  AssigneeSchema,
  ReminderSchema,
  ScheduleColourSchema,
  ScheduleNotesSchema,
  type ScheduleItem,
} from '../../domain/entities/schedule.ts';
import {
  addWorkingDays,
  endDateFor,
  nextWorkingDay,
  workingDaysBetween,
  type WorkCalendar,
} from '../../domain/work-calendar.ts';
import { deleteWrite, expectUnchanged, updateWrite } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import { listVisibleScheduleItems } from './list-schedule.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { NotFoundError } from '../../ports/repository.ts';
import { loadJobCalendar } from './workday-exceptions.ts';

export interface SaveScheduleItemContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

/** Fields a caller may set, on create or update. Dates are handled separately. */
const DetailsSchema = z.object({
  title: z.string().trim().min(1, 'Give the item a title').max(200),
  colour: ScheduleColourSchema.optional(),
  phaseId: z.string().min(1).nullish(),
  tagIds: z.array(z.string().min(1)).optional(),
  assignees: z.array(AssigneeSchema).optional(),
  /** Plant booked onto this work (SCRUM-211). */
  equipmentIds: z.array(IdSchema).optional(),
  /** Materials this work needs (SCRUM-210): a list, not a purchase. */
  materials: z
    .array(
      z.object({
        catalogItemId: IdSchema,
        quantityThousandths: z.number().int().min(0).default(1_000),
      }),
    )
    .optional(),
  progress: z.number().int().min(0).max(100).optional(),
  completed: z.boolean().optional(),
  reminder: ReminderSchema.optional(),
  notes: ScheduleNotesSchema.optional(),
  /**
   * Who may see this item.
   *
   * `internal` must stay true, which the spec leaves open and the security
   * rules settle: updating an item requires the existing document to be
   * visible to the person updating it, so an item hidden from internal staff
   * could not afterwards be edited, re-shared or deleted by anyone in the
   * company building the job. It would be reachable only by the sub it was
   * shown to. That is recorded as an assumption in
   * `docs/features/02-schedule.md` §3.
   *
   * `private` is not offered on a schedule item. The spec's item detail
   * lists Internal/Subs/Owner, and a date only the author can see is not a
   * schedule — it is a note, which is a different record.
   */
  visibility: VisibilitySchema.refine((value) => value.internal, {
    message: 'A schedule item has to stay visible to the company building the job',
  })
    .refine((value) => !value.private, {
      message: 'A schedule item cannot be private to one person',
    })
    .optional(),
});

const DatesSchema = z.object({
  startDate: DateOnlySchema.optional(),
  endDate: DateOnlySchema.optional(),
  workDays: z.number().int().min(1, 'An item needs at least one work day').optional(),
});

export const CreateScheduleItemInputSchema = DetailsSchema.merge(DatesSchema);
export type CreateScheduleItemInput = z.input<typeof CreateScheduleItemInputSchema>;

export const UpdateScheduleItemInputSchema = DetailsSchema.partial().merge(DatesSchema);
export type UpdateScheduleItemInput = z.input<typeof UpdateScheduleItemInputSchema>;

export class ScheduleItemValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`That schedule item needs attention — ${summary}`);
    this.name = 'ScheduleItemValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Validate against one of this module's schemas, reporting per field.
 *
 * Exported so the plan use case validates an item edit exactly as this one
 * does — same messages, same field names — rather than growing a second
 * opinion about what a valid item is.
 */
export function parseScheduleItemInput<T extends z.ZodTypeAny>(
  schema: T,
  input: unknown,
): z.output<T> {
  const parsed = schema.safeParse(input);
  if (parsed.success) return parsed.data;

  const fieldErrors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    fieldErrors[issue.path.join('.') || 'form'] ??= issue.message;
  }
  throw new ScheduleItemValidationError(fieldErrors);
}

export interface Span {
  readonly startDate: string;
  readonly workDays: number;
  readonly endDate: string;
}

/**
 * Work out the span from whatever two of the three the caller supplied.
 *
 * Any two of start, duration and end determine the third, which is what the
 * item modal and the Gantt both rely on: typing an end date changes the
 * duration, dragging a bar keeps it. The calendar is the arbiter, so the
 * answer accounts for weekends and this job's workday exceptions.
 */
export function resolveSpan(
  calendar: WorkCalendar,
  requested: { startDate?: string; endDate?: string; workDays?: number },
): Span {
  const { startDate, endDate, workDays } = requested;

  if (startDate === undefined && endDate === undefined) {
    throw new ScheduleItemValidationError({
      startDate: 'Give the item a start date or an end date',
    });
  }

  if (startDate !== undefined && endDate !== undefined) {
    if (compareDateOnly(startDate, endDate) > 0) {
      throw new ScheduleItemValidationError({
        endDate: 'That end date is before the start date',
      });
    }
    // Both ends are pulled onto working days, as every other branch here
    // does. Taking the two dates verbatim let an item be stored starting on
    // a Sunday and ending on a Saturday — days nobody works — which drew a
    // bar wider than the work in it and made two spans covering identical
    // work compare as different. The duration is unaffected: a count of
    // working days does not change when the ends are rolled onto working
    // days.
    const first = nextWorkingDay(calendar, startDate);
    const derived = workingDaysBetween(calendar, first, endDate);
    if (derived < 1) {
      // Both dates fall on non-working days with nothing worked between them.
      throw new ScheduleItemValidationError({
        endDate: 'No working days fall in that range on this job calendar',
      });
    }
    // A client that sends all three gets checked rather than silently
    // corrected: one of the values it is showing the user would be wrong.
    if (workDays !== undefined && workDays !== derived) {
      throw new ScheduleItemValidationError({
        workDays: `Those dates and that duration disagree — ${derived} working days fall between them`,
      });
    }
    return { startDate: first, workDays: derived, endDate: endDateFor(calendar, first, derived) };
  }

  if (startDate !== undefined) {
    const days = workDays ?? 1;
    const first = nextWorkingDay(calendar, startDate);
    return { startDate: first, workDays: days, endDate: endDateFor(calendar, first, days) };
  }

  // End and duration: count backwards to find the start.
  const days = workDays ?? 1;
  const last = endDate!;
  const first = days === 1 ? last : addWorkingDays(calendar, last, -(days - 1));
  return { startDate: nextWorkingDay(calendar, first), workDays: days, endDate: last };
}

function repository(context: SaveScheduleItemContext) {
  return context.data.collection<ScheduleItem>(
    { companyId: context.companyId, jobId: context.jobId },
    'scheduleItems',
  );
}

/** Drop an empty string so an untouched optional field is absent, not blank. */
function optional(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export async function createScheduleItem(
  context: SaveScheduleItemContext,
  input: CreateScheduleItemInput,
): Promise<ScheduleItem> {
  const parsed = parseScheduleItemInput(CreateScheduleItemInputSchema, input);
  const calendar = await loadJobCalendar(context);
  const span = resolveSpan(calendar, parsed);
  const now = context.clock.now().toISOString();

  const item: ScheduleItem = {
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.title,
    colour: parsed.colour ?? 'blue',
    phaseId: optional(parsed.phaseId),
    tagIds: parsed.tagIds ?? [],
    assignees: parsed.assignees ?? [],
    equipmentIds: parsed.equipmentIds ?? [],
    materials: parsed.materials ?? [],
    startDate: span.startDate,
    workDays: span.workDays,
    endDate: span.endDate,
    progress: parsed.progress ?? 0,
    completed: parsed.completed ?? false,
    reminder: parsed.reminder ?? 'none',
    predecessors: [],
    confirmations: {},
    shifts: [],
    notes: parsed.notes ?? {},
    // Internal until someone decides otherwise: a half-planned date reaching
    // an owner's portal is worse than one they cannot see yet.
    visibility: parsed.visibility ?? INTERNAL_ONLY,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  await repository(context).create(item);
  return item;
}

export async function updateScheduleItem(
  context: SaveScheduleItemContext,
  id: string,
  input: UpdateScheduleItemInput,
): Promise<ScheduleItem> {
  const parsed = parseScheduleItemInput(UpdateScheduleItemInputSchema, input);
  const repo = repository(context);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('scheduleItems', id);

  const touchesDates =
    parsed.startDate !== undefined || parsed.endDate !== undefined || parsed.workDays !== undefined;

  let span: Span = {
    startDate: existing.startDate,
    workDays: existing.workDays,
    endDate: existing.endDate,
  };

  if (touchesDates) {
    const calendar = await loadJobCalendar(context);
    span = resolveSpan(calendar, {
      startDate: parsed.startDate ?? existing.startDate,
      // An explicit end date is what the caller is changing, so it wins and
      // the duration is recomputed. Moving only the start keeps the duration,
      // which is what dragging a bar along the timeline means.
      endDate: parsed.endDate,
      workDays: parsed.endDate === undefined ? (parsed.workDays ?? existing.workDays) : undefined,
    });
  }

  const now = context.clock.now().toISOString();
  const next = applyItemPatch(existing, parsed, span, { at: now, uid: context.uid });

  const { id: _id, ...patch } = next;
  await repo.update(id, patch);
  return next;
}

/**
 * Apply a parsed patch and a resolved span to an item.
 *
 * Pure, and exported, because two use cases have to agree about what editing
 * an item means. `updateScheduleItem` writes one document; the plan use case
 * writes the same change plus every date it pushes, in one batch. If they
 * each built the next item their own way they would drift — one of them
 * would forget to clear `completedAt` on a reopen, and the bug would show up
 * only on whichever path somebody happened to use.
 */
export function applyItemPatch(
  existing: ScheduleItem,
  parsed: {
    title?: string;
    colour?: ScheduleItem['colour'];
    phaseId?: string | null;
    tagIds?: string[];
    assignees?: ScheduleItem['assignees'];
    equipmentIds?: ScheduleItem['equipmentIds'];
    materials?: ScheduleItem['materials'];
    visibility?: ScheduleItem['visibility'];
    progress?: number;
    completed?: boolean;
    reminder?: ScheduleItem['reminder'];
    notes?: ScheduleItem['notes'];
  },
  span: Span,
  by: { at: string; uid: string },
): ScheduleItem {
  const completed = parsed.completed ?? existing.completed;
  const justCompleted = completed && !existing.completed;
  const justReopened = !completed && existing.completed;

  return {
    ...existing,
    title: parsed.title ?? existing.title,
    colour: parsed.colour ?? existing.colour,
    phaseId: parsed.phaseId === undefined ? existing.phaseId : optional(parsed.phaseId),
    tagIds: parsed.tagIds ?? existing.tagIds,
    assignees: parsed.assignees ?? existing.assignees,
    equipmentIds: parsed.equipmentIds ?? existing.equipmentIds,
    materials: parsed.materials ?? existing.materials,
    visibility: parsed.visibility ?? existing.visibility,
    startDate: span.startDate,
    workDays: span.workDays,
    endDate: span.endDate,
    // Completing an item means the work is done, so progress follows unless
    // the caller says otherwise in the same breath — and **reopening one
    // means it is not**, which is the half that was missing. Ticking an
    // item on the calendar took it to 100% and unticking left it there, so
    // an item that was plainly not finished showed a full bar.
    //
    // Reopening resets to zero rather than restoring what the progress was
    // before completion: nothing stores that, and inventing a number would
    // be worse than the honest one. Somebody who meant "not finished, three
    // quarters there" says so in the same edit, and that still wins.
    progress: parsed.progress ?? (justCompleted ? 100 : justReopened ? 0 : existing.progress),
    completed,
    completedAt: justCompleted ? by.at : justReopened ? undefined : existing.completedAt,
    completedBy: justCompleted ? by.uid : justReopened ? undefined : existing.completedBy,
    reminder: parsed.reminder ?? existing.reminder,
    notes: parsed.notes ?? existing.notes,
    updatedAt: by.at,
    updatedBy: by.uid,
  };
}

/**
 * Every item on the job this caller may see, soonest first.
 *
 * Delegates to `listVisibleScheduleItems`, which is where the query shape
 * lives. It used to be an unconstrained list, and the `scheduleItems` list
 * rule used to allow one — until the rule was narrowed to check each
 * returned document's audience. Every schedule use case in this package
 * reads through here, so one change fixed them all.
 *
 * What an internal caller no longer sees is **another person's private
 * item**. That is not a loss: the `get` and `update` rules already refused
 * it, so a cascade could never have moved one anyway — it would have read
 * the item and then failed to write it.
 */
export async function listScheduleItems(context: SaveScheduleItemContext): Promise<ScheduleItem[]> {
  return listVisibleScheduleItems({
    data: context.data,
    companyId: context.companyId,
    jobId: context.jobId,
    uid: context.uid,
  });
}

/**
 * Delete an item, and unlink anything that depended on it.
 *
 * The unlinking is the important half. A predecessor pointing at an item
 * that no longer exists makes the whole schedule uncomputable: every
 * cascade, the critical path and the re-settle all walk the dependency
 * graph, and all of them throw on a reference they cannot resolve. One
 * deletion would quietly disable the schedule until somebody found the
 * dangling link by hand.
 *
 * One batch, so the item and the links to it go together.
 */
export async function deleteScheduleItem(
  context: SaveScheduleItemContext,
  id: string,
): Promise<void> {
  const repo = repository(context);
  if (!(await repo.get(id))) throw new NotFoundError('scheduleItems', id);

  const scope = { companyId: context.companyId, jobId: context.jobId };
  const dependents = (await repo.list()).items.filter((item) =>
    item.predecessors.some((predecessor) => predecessor.itemId === id),
  );
  const now = context.clock.now().toISOString();

  await context.data.commit([
    deleteWrite(scope, 'scheduleItems', id),
    ...dependents.map((item) =>
      updateWrite<ScheduleItem>(
        scope,
        'scheduleItems',
        item.id,
        {
          predecessors: item.predecessors.filter((predecessor) => predecessor.itemId !== id),
          updatedAt: now,
          updatedBy: context.uid,
        },
        expectUnchanged(item),
      ),
    ),
  ]);
}
